import { scale } from "@productlane/lib"
import { Switch } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import { UpgradeButton } from "@/components/upgrade-button"
import { analytics } from "@/lib/analytics"
import { useRequiredPlans } from "@/lib/plans"
import { useSession } from "@/lib/session"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { SettingsHeader } from "../settings-header"
import { SettingItem } from "../settings-switches/setting-item"
import { SettingsContainer } from "../settings-switches/settings-container"

export function AssignmentsSettingsPage() {
	const workspace = useZeroWorkspace()
	const zero = useZero()
	const { session } = useSession()

	const { passes: hasRequiredPlan } = useRequiredPlans(scale.andUp)

	if (!workspace) return null

	return (
		<>
			<MetaInfo title="Assignments" />

			<SettingsHeader
				title="Assignments"
				subtitle="Control how threads are routed to team members and how contacts are grouped into companies."
			/>

			<SettingsContainer>
				<SettingItem
					title="Auto-assign to Linear owner"
					description="Threads will be assigned to the customer's owner if they're a member of this workspace."
					action={
						<>
							{hasRequiredPlan ? (
								<Switch
									name="autoAssignFromLinearOwner"
									checked={workspace.autoAssignFromLinearOwner ?? false}
									onCheckedChange={async (value) => {
										if (!hasRequiredPlan) {
											toast.error({
												title: "Scale plan required",
											})
											return
										}

										if (session?.workspaceId) {
											await mutateOk(
												zero.mutate(
													mutators.workspaces.update({
														id: session.workspaceId,
														autoAssignFromLinearOwner: value,
													}),
												),
											)

											analytics.track("update-assignment-settings")
										}
									}}
								/>
							) : (
								<UpgradeButton required={scale.andUp} />
							)}
						</>
					}
				/>
				<SettingItem
					className="pt-4"
					title="Follow latest email sender"
					description="Reassigns the thread's contact to whoever sent the most recent inbound email."
					action={
						<Switch
							name="autoSwitchContactToLatestSender"
							checked={workspace.autoSwitchContactToLatestSender ?? false}
							onCheckedChange={async (value) => {
								if (session?.workspaceId) {
									await mutateOk(
										zero.mutate(
											mutators.workspaces.update({
												id: session.workspaceId,
												autoSwitchContactToLatestSender: value,
											}),
										),
									)

									analytics.track("update-assignment-settings")
								}
							}}
						/>
					}
				/>
				<SettingItem
					className="pt-4"
					title="Auto-assign contacts to companies by domain"
					description="Automatically groups contacts into companies based on their email domain."
					action={
						<Switch
							name="domainCompanyAssignmentEnabled"
							checked={workspace.domainCompanyAssignmentEnabled ?? true}
							onCheckedChange={async (value) => {
								if (session?.workspaceId) {
									await mutateOk(
										zero.mutate(
											mutators.workspaces.update({
												id: session.workspaceId,
												domainCompanyAssignmentEnabled: value,
											}),
										),
									)

									analytics.track("update-assignment-settings")
								}
							}}
						/>
					}
				/>
			</SettingsContainer>
		</>
	)
}
